import type { LanguageCode, UnitSystem, User } from '@dishly/shared';
import { api, apiEvents, isNetworkError } from './api';
import { clearUserData, syncLists, syncSummaries } from './sync';
import { usePrefs } from '../stores/prefs';
import { setSessionUser, useSession } from '../stores/session';

async function switchUser(user: User | null): Promise<void> {
  const previous = useSession.getState().user;
  setSessionUser(user);
  if (previous?.id !== user?.id) {
    await clearUserData();
    void syncSummaries({ full: true });
    void syncLists();
  }
}

/** Called after a successful login: adopts the account's saved preferences. */
export async function completeLogin(user: User): Promise<void> {
  usePrefs.setState({ units: user.preferredUnits, language: user.language });
  await switchUser(user);
}

export async function logOut(): Promise<void> {
  try {
    await api('/auth/logout', { method: 'POST' });
  } catch {
    // Signing out locally still matters when offline.
  }
  await switchUser(null);
}

/** Checks the session with the server; keeps the cached user when offline. */
export async function refreshSession(): Promise<void> {
  try {
    const { user } = await api<{ user: User | null }>('/me');
    await switchUser(user);
  } catch (error) {
    if (isNetworkError(error)) useSession.setState({ ready: true });
    else console.error('[session]', error);
  }
}

apiEvents.addEventListener('unauthorized', () => {
  if (useSession.getState().user) void switchUser(null);
});

/** Updates units/language on this device and, when signed in, on the account. */
export async function updatePreferences(changes: { units?: UnitSystem; language?: LanguageCode }): Promise<void> {
  usePrefs.setState(changes);
  if (!useSession.getState().user) return;
  try {
    const { user } = await api<{ user: User }>('/me', {
      method: 'PATCH',
      body: { preferredUnits: changes.units, language: changes.language },
    });
    setSessionUser(user);
  } catch {
    // The local preference still applies; it is saved to the account next time.
  }
}
